// ####################################################################################################################
// # Copyright (C) 2023 Semactic SRL - All Rights Reserved
// # Unauthorized copying of this file, or its contents, even partially, through any medium is strictly prohibited.
// # Proprietary and confidential. Semactic SRL is the sole owner of this code.
// # Written by Fourneaux Thibaut (thibaut.fourneaux@semactic.com) under Semactic SEO Software Project.
// ####################################################################################################################

import { defineStore } from 'pinia';
import axios from 'axios';
import { orderArray, setBeforeAfter, updatePosition, checkForChange } from '@/utils/arrays';
import { useNotificationStore } from '@/store/global/notification';
import { useUsersStore } from '@/store/global/users';
import { decompressJson } from '@/utils/unzip';
import { filterMatch } from '@/utils/autocomplete';
import { applyFilter } from '@/utils/filterUtils';

export const useRecommendationsStore = defineStore('recommendations', {
	state: () => ({
		recommendations: [],
		recommendationsClone: [],
		tasks: [],
		tasksClone: [],
		viewPreferences: 'grid',
		loading: true,
		filterData: {},
		generatedRecoLang: 'en',
		limit_onpage: 10,
		limit_new_content: 10,
		limit_backlinks_reco: 0,
		limit_technical_reco: 0,
		limit_geo_reco: 0,
		limit_ux_reco: 0,
		recoTypeCatalog: [],
	}),
	getters: {
		unlisted(state) {
			return state.recommendations.filter(item => item.status === 0);
		},
		todo(state) {
			return state.recommendations.filter(item => item.status === 1);
		},
		doing(state) {
			return state.recommendations.filter(item => item.status === 2);
		},
		done(state) {
			return state.recommendations.filter(item => item.status === 3);
		},
		abandoned(state) {
			return state.recommendations.filter(item => item.status === 4);
		},
		deleted(state) {
			return state.recommendations.filter(item => item.status === 5);
		},
		users: state => {
			const allUsers = state.recommendations.flatMap(rec => (rec.users ? rec.users : [])).filter(user => user != null); // This line filters out null or undefined users
			const uniqueUsers = Array.from(new Set(allUsers.map(user => JSON.stringify(user)))).map(str => JSON.parse(str));
			return uniqueUsers;
		},
	},
	actions: {
		async fetchData(selectedMarket) {
			this.loading = true;

			const response = await axios.post(
				'vuejs/get_recommendations_lv1',
				{
					market_id: selectedMarket,
				},
				{ withCredentials: true },
			);
			this.generatedRecoLang = response.data.generatedRecoLang;

			const deepClone = JSON.parse(JSON.stringify(response.data.data));
			this.recommendations = response.data.data;
			this.recommendationsClone = deepClone;
			this.loading = false;
		},
		async fetchTasks(selectedMarket) {
			this.loading = true;

			const response = await axios
				.post(
					'vuejs/get_recommendations_tasks',
					{
						market_id: selectedMarket,
					},
					{ withCredentials: true },
				)
				.then(response => {
					var base64String = response.data.data['base64(zip(o))'];
					if (typeof base64String === 'string') {
						var decompressedData = decompressJson(base64String);
					} else {
						console.error('The extracted data is not a string:', base64String);
					}
					this.tasks = decompressedData;
					this.tasksClone = decompressedData;
					this.loading = false;
				});

			return this.tasks;
		},
		async fetchRecoTypeCatalog() {
			try {
				const response = await axios.post(
					'vuejs/get_reco_type_catalog',
					{},
					{ withCredentials: true },
				);

				if (response.data?.success) {
					this.recoTypeCatalog = Array.isArray(response.data.data) ? response.data.data : [];
					return this.recoTypeCatalog;
				}

				this.recoTypeCatalog = [];
				return [];
			} catch (error) {
				console.error('Failed to fetch recommendation types catalog:', error);
				this.recoTypeCatalog = [];
				return [];
			}
		},
		syncAssignedUserToRecommendation(groupId, userId, taskId) {
			if (taskId && Array.isArray(this.tasks) && !userId) {
				const task = this.tasks.find(t => t.id === taskId || t.id == taskId);
				if (task) {
					const previousUserId = task.who;
					task.who = null;
					task.users = [];
					if (groupId && previousUserId) {
						const reco = this.recommendations.find(r => r.id === groupId || r.id == groupId);
						if (reco) {
							if (!Array.isArray(reco.users)) reco.users = [];
							reco.users = reco.users.filter(u => u && (u.id !== previousUserId && u.id != previousUserId));
						}
					}
					return;
				}
			}
			if (!userId) return;
			const usersStore = useUsersStore();
			const user = usersStore.users.find(u => u.id === userId || u.id == userId);
			if (!user) return;
			const userObj = {
				id: user.id,
				first_name: user.first_name,
				last_name: user.last_name,
				avatar: user.avatar,
				email: user.email,
			};
			if (taskId && Array.isArray(this.tasks)) {
				const task = this.tasks.find(t => t.id === taskId || t.id == taskId);
				if (task) {
					task.who = userId;
					task.users = [userObj];
				}
			}
			if (groupId) {
				const reco = this.recommendations.find(r => r.id === groupId || r.id == groupId);
				if (reco && Array.isArray(this.tasks)) {
					const groupTasks = this.tasks.filter(t => t.group_id === groupId || t.group_id == groupId);
					const assigneeIds = [...new Set(groupTasks.map(t => t.who).filter(Boolean))];
					reco.users = assigneeIds
						.map(uid => usersStore.users.find(u => u.id === uid || u.id == uid))
						.filter(Boolean)
						.map(u => ({
							id: u.id,
							first_name: u.first_name,
							last_name: u.last_name,
							avatar: u.avatar,
							email: u.email,
						}));
				}
			}
		},
		async getImplementationAndDates(groupId) {
			const response = await axios.post(
				'vuejs/get_graph_implementations',
				{
					group_id: groupId,
				},
				{ withCredentials: true },
			);

			if (response.data) {
				this.implementations = response.data.implementations;
				this.graphRange.from = response.data.date_range_start;
				this.graphRange.to = response.data.date_range_end;
			}
		},
		async setImplementation(recoId) {
			const response = await axios.post(
				'vuejs/set_reco_implemented',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async undoImplementation(recoId) {
			const response = await axios.post(
				'vuejs/undo_reco_implemented',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async setValidation(recoId) {
			const response = await axios.post(
				'vuejs/set_reco_validated',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async undoValidation(recoId) {
			const response = await axios.post(
				'vuejs/undo_reco_validated',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async setAbandon(recoId) {
			const response = await axios.post(
				'vuejs/set_reco_abandoned',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async undoAbandon(recoId) {
			const response = await axios.post(
				'vuejs/undo_reco_abandoned',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);
		},
		async updateMessage(recoId, message) {
			const response = await axios.post(
				'vuejs/update_reco_message',
				{
					reco_id: recoId,
					message: message,
				},
				{ withCredentials: true },
			);
		},
		// status
		async updateStatus(recoId, destination) {
			try {
				const response = await axios.post(
					'vuejs/set_reco_lv1_status',
					{
						reco_id: recoId,
						status: destination,
					},
					{ withCredentials: true },
				);
				return response.data.success === true;
			} catch (error) {
				console.error('Failed to update status:', error);
				return false;
			}
		},
		async changeStatus(recoId, destination, dropResult, t) {
			const reco = this.recommendations.find(item => item.id === recoId);
			if (!reco) return;

			const previousStatus = reco.status;
			const previousRecommendations = JSON.parse(JSON.stringify(this.recommendations));
			let updatedRecommendations = [];

			// update column locally first (optimistic update)
			this.setOrderWhenMovingCard('remove', recoId, reco.status).forEach(element => {
				this.updateRecommendationPositionArr(updatedRecommendations, element);
			});
			reco.status = destination;

			// update order and call API
			const success = await this.changeOrder(destination, dropResult, recoId, updatedRecommendations, true);

			// rollback if API failed
			if (!success) {
				console.error(`Failed to move task ${recoId} to status ${destination}. Previous status: ${previousStatus}. API call failed.`);
				this.recommendations = previousRecommendations;
				this.recommendationsClone = JSON.parse(JSON.stringify(previousRecommendations));
				useNotificationStore().open('error', 'Failed to update task status. Please try again.');
			}
		},
		async changeCardStatus(recoId, destination) {
			const reco = this.recommendations.find(item => item.id === recoId);
			if (!reco) return;

			const previousStatus = reco.status;
			reco.status = destination;

			const success = await this.updateStatus(recoId, destination);
			if (!success) {
				console.error(`Failed to update card status for task ${recoId} to status ${destination}. Previous status: ${previousStatus}. API call failed.`);
				reco.status = previousStatus;
				useNotificationStore().open('error', 'Failed to update task status. Please try again.');
			}
		},
		// order
		async updateOrder(recommendations) {
			const updateOrderArray = recommendations.map(item => ({
				id: item.id,
				position: item.position,
			}));

			const response = await axios.post(
				'vuejs/update_recommendations_order',
				{
					data: updateOrderArray,
				},
				{
					withCredentials: true,
				},
			);
		},
		async changeOrder(status, dropResult, recoId = null, updatedRecommendations = [], changeStatus = false) {
			const { removedIndex, addedIndex } = dropResult;

			const deepClone = JSON.parse(JSON.stringify(this.recommendations));
			const recommendationsByStatus = this.splitRecommendationsByStatus(this.recommendations);
			let statusRecommendations = recommendationsByStatus[status];

			// moved from another column
			if (addedIndex != null && recoId != null) {
				statusRecommendations = updatePosition(statusRecommendations, addedIndex, removedIndex, recoId);
			}
			// moved within the same column
			else if (removedIndex !== null && addedIndex !== null && recoId == null) {
				statusRecommendations = updatePosition(statusRecommendations, addedIndex, removedIndex);
			}

			// order items by position and set if null (statuts: 0=unlisted, 1=todo, 2=doing, 3=done, 4=abandoned, 5=deleted)
			for (let i = 0; i <= 5; i++) {
				recommendationsByStatus[i] = orderArray(recommendationsByStatus[i] || []);
			}

			// rebuild one array and update order if needed
			let finalRecommendations = Object.values(recommendationsByStatus).flat();
			let deepDiferences = checkForChange(deepClone, finalRecommendations);
			if (deepDiferences.length > 0) {
				this.updateOrder(deepDiferences);
			}

			this.recommendations = finalRecommendations;
			this.recommendationsClone = JSON.parse(JSON.stringify(finalRecommendations));

			// call API to update status if needed (status already changed in changeStatus)
			if (changeStatus && recoId != null) {
				const success = await this.updateStatus(recoId, status);
				return success;
			}

			return true;
		},
		customSort(a, b) {
			if (a.reco_id_before === 0) return -1;
			if (b.reco_id_before === 0) return 1;
			if (a.reco_id_after === 0) return 1;
			if (b.reco_id_after === 0) return -1;
			if (a.reco_id_after !== b.id) return 1;
			if (b.reco_id_before !== a.id) return -1;
			return a.reco_id_after - b.id;
		},
		setOrderWhenMovingCard(cases, recoId, status) {
			const recommendationsByStatus = this.splitRecommendationsByStatus(this.recommendations);
			const recoIndex = recommendationsByStatus[status].findIndex(item => item.id === recoId);
			const reco = recommendationsByStatus[status][recoIndex];
			const changedItem = [];

			switch (cases) {
				case 'remove':
					// set previous item
					if (recommendationsByStatus[status][recoIndex - 1]) {
						recommendationsByStatus[status][recoIndex - 1].reco_id_after = reco.reco_id_after;
						changedItem.push(recommendationsByStatus[status][recoIndex - 1]);
					}
					// set next item
					if (recommendationsByStatus[status][recoIndex + 1]) {
						recommendationsByStatus[status][recoIndex + 1].reco_id_before = reco.reco_id_before;
						changedItem.push(recommendationsByStatus[status][recoIndex + 1]);
					}

					// reset current item
					reco.reco_id_before = 0;
					reco.reco_id_after = 0;

					changedItem.push(reco);

					return changedItem;

				case 'add':
					// set previous item
					if (recommendationsByStatus[status][recoIndex - 1]) {
						recommendationsByStatus[status][recoIndex - 1].reco_id_after = reco.id;
						reco.reco_id_before = recommendationsByStatus[status][recoIndex - 1].id;
						changedItem.push(recommendationsByStatus[status][recoIndex - 1]);
					} else {
						reco.reco_id_before = 0;
					}

					// set next item
					if (recommendationsByStatus[status][recoIndex + 1]) {
						recommendationsByStatus[status][recoIndex + 1].reco_id_before = reco.id;
						reco.reco_id_after = recommendationsByStatus[status][recoIndex + 1].id;
						changedItem.push(recommendationsByStatus[status][recoIndex + 1]);
					} else {
						reco.reco_id_after = 0;
					}

					// add current item with the new values
					changedItem.push(reco);
					return changedItem;
			}
		},
		updateRecommendationPositionArr(arr, element) {
			const index = arr.findIndex(el => el.id === element.id);
			if (index !== -1) {
				arr[index] = element;
			} else {
				arr.push(element);
			}
		},
		splitRecommendationsByStatus(recommendations) {
			return recommendations.reduce((result, item) => {
				if (!result[item.status]) {
					result[item.status] = [];
				}

				result[item.status].push(item);
				return result;
			}, {});
		},
		async updateSeoTitle(recoId, seoTitle) {
			const response = await axios.post(
				'vuejs/update_reco_seo_title',
				{
					reco_id: recoId,
					seo_title: seoTitle,
				},
				{ withCredentials: true },
			);

			// if (response.data.success) {
			this.recommendations.filter(recommendations => recommendations.id == recoId)[0].seo_title = seoTitle;
			// }
		},
		async updateMetaDescription(recoId, metaDescription) {
			const response = await axios.post(
				'vuejs/update_reco_meta_description',
				{
					reco_id: recoId,
					meta_description: metaDescription,
				},
				{ withCredentials: true },
			);
			// if (response.data.success) {
			this.recommendations.filter(recommendations => recommendations.id == recoId)[0].meta_description = metaDescription;
			// }
		},
		async updateDescription(recoId, description) {
			const response = await axios.post(
				'vuejs/update_reco_description',
				{
					reco_id: recoId,
					description: description,
				},
				{ withCredentials: true },
			);
			// if (response.data.success) {
			this.recommendations.filter(recommendations => recommendations.id == recoId)[0].description = description;
			// }
		},

		async createRecommendations({ recommendations, market_id, card_id = null, custom_card = {}, table = 'recommendations' }) {
			// Convert the deadline date to a timestamp in seconds for each recommendation
			for (let r of recommendations) {
				if (r.deadline) {
					r.deadline = Math.floor(new Date(r.deadline).getTime() / 1000);
				}
				// If the user is set, set the who and user_in_charge properties to the user object
				if (r.user) {
					r.who = r.user;
					r.user_in_charge = r.user;
				}
			}
			const response = await axios
				.post(
					'vuejs/create_recommendations',
					{
						recommendations: recommendations,
						market_id: market_id,
						card_id: card_id,
						table: table,
						custom_card: custom_card,
					},
					{ withCredentials: true },
				)
				.then(async response => {
					this.fetchData(market_id);
					if (response.data.success == true) {
						useNotificationStore().open('success', 'Recommendation(s) created');
					} else {
						useNotificationStore().open('error', `${response.data.message}`);
					}
					// wait 0.5seconds
					await new Promise(resolve => setTimeout(resolve, 500));
					this.loading = false;
				});
		},
        async deleteRecoWithKeyword(market_id, group_id) {
            const response = await axios.post(
                'vuejs/delete_reco_with_keyword',
                {
                    market_id: market_id,
                    unique_group_id: group_id,
                },
                { withCredentials: true },
            );

            if (response.data.success) {
                this.fetchData(market_id);
            }
        },
		async updateRecommendations(recommendations, market_id, card_id = null) {
			const response = await axios
				.post(
					'vuejs/update_recommendations',
					{
						recommendations: recommendations,
						market_id: market_id,
						card_id: card_id,
					},
					{ withCredentials: true },
				)
				.then(response => {
					this.fetchData(market_id);
					if (response.data.success == true) {
						useNotificationStore().open('success', 'Recommendation(s) created');
					} else {
						useNotificationStore().open('error', `${response.data.message}`);
					}
				});
		},

		async updateDeadline(recoId, deadline) {
			const response = await axios.post(
				'vuejs/update_reco_deadline',
				{
					reco_id: recoId,
					deadline: deadline,
				},
				{ withCredentials: true },
			);
			// if (response.data.success) {
			// this.recommendations.filter(recommendations => recommendations.id == recoId)[0].deadline = deadline;
			// }
		},
		async updateDeadlineTask(taskId, deadline) {
			const response = await axios.post(
				'vuejs/update_task_deadline',
				{
					task_id: taskId,
					deadline: deadline,
				},
				{ withCredentials: true },
			);
		},
		async generateRecommendations({
			market_id,
			labels_id,
			force = 1,
			onpage_reco = this.limit_onpage > 0 ? 1 : 0,
			new_content_reco = this.limit_new_content > 0 ? 1 : 0,
			backlink_reco = this.limit_backlinks_reco > 0 ? 1 : 0,
			technical_reco = this.limit_technical_reco > 0 ? 1 : 0,
			geo_reco = this.limit_geo_reco > 0 ? 1 : 0,
			ux_reco = this.limit_ux_reco > 0 ? 1 : 0,
			limit_onpage = this.limit_onpage,
			limit_new_content = this.limit_new_content,
			limit_backlinks_reco = this.limit_backlinks_reco,
			limit_technical_reco = this.limit_technical_reco,
			limit_geo_reco = this.limit_geo_reco,
			limit_ux_reco = this.limit_ux_reco,
			card_id = 'definitive',
			keyword_id = null,
			page_url = null,
			type_reco = 'recommendations',
			tMessage = null,
			question_id = null,
			all_site = false,
			selected_reco_type_ids = null,
		}) {
				// labels_id is an array
				if (tMessage === null) {
					useNotificationStore().open('info', 'Generating recommendations.. It can take up to 10 minutes, you will get a notification once it is done.');
					tMessage = {
						success: 'Recommendations generated successfully',
						error: 'An error occurred while generating recommendations',
						noReco: 'Nothing was generated for your parameters.',
					};
				} else {
					useNotificationStore().open('info', tMessage.waiting);
				}

			try {
				const response = await axios.post(
					'vuejs/generate_recommendations',
					{
						market_id: market_id,
						labels_id: labels_id,
						force: force,
						onpage_reco: onpage_reco,
						new_content_reco: new_content_reco,
						backlink_reco: backlink_reco,
						technical_reco: technical_reco,
						geo_reco: geo_reco,
						ux_reco: ux_reco,
						limit_onpage: limit_onpage,
						limit_new_content: limit_new_content,
						limit_backlinks_reco: limit_backlinks_reco,
						limit_technical_reco: limit_technical_reco,
						limit_geo_reco: limit_geo_reco,
						limit_ux_reco: limit_ux_reco,
						type_reco: type_reco,
						keyword_id: keyword_id,
						page_url: page_url,
						question_id: question_id,
						all_site: all_site,
						selected_reco_type_ids: selected_reco_type_ids,
					},
					{
						withCredentials: true,
						nonInterruptible: true,
					},
				)

				if (response.data.success) {

					let number_of_recommendations = response.data.number_of_recommendations || 0;

					if (number_of_recommendations === 0) {
						number_of_recommendations =
							response.data.nb_new_content_reco_added +
							response.data.nb_onpage_reco_added +
							response.data.nb_technical_reco_added +
							response.data.nb_backlinks_reco_added;
					}

					if (number_of_recommendations === 0) {
						useNotificationStore().open('info', tMessage.noReco || 'Nothing was generated for your parameters.');
					} else {
						useNotificationStore().open(
							'success',
							`
${number_of_recommendations} ${tMessage.success}`,
						);
					}

					this.fetchData(market_id);
					return false;
				}
				else {
					useNotificationStore().open('error', `${response.data.message}`);
					return false;
				}
			}
			catch (error) {
				// Gérer l'erreur
				useNotificationStore().open('error', tMessage.error);
				console.error(error);
				return false;
			}
		},
		/**
		* Handles filtering of a dataset based on the input query, with support for
		* both regular string matching and numeric conditions (>, <, =).
		* 
		* @param {Array} dataset - The dataset to filter (e.g., recommendations).
		* @param {Object} filter - The filter object containing key and name.
		* @returns {Array} - The filtered dataset.
		*/
		applyFilter(source, filter) {
			if (!filter || !filter.item) {
				return source;
			}

			const queryValue = filter.item.name ? filter.item.name.toLowerCase() : '';
			const querySelector = filter.item.key;

			if (!queryValue && !querySelector) {
				return source;
			}

			let data = null;

			// Check if the input includes a condition (like ":volume > 30")
			const conditionMatch = queryValue.match(/:([a-zA-Z]+)\s*([<>]=?|=)\s*(\d+)/);

			if (conditionMatch) {
				// Extract the field (e.g., 'volume'), the operator (e.g., '>'), and the value (e.g., 30)
				const field = conditionMatch[1];
				const operator = conditionMatch[2];
				const conditionValue = conditionMatch[3];

				// Handle numeric filtering for fields like 'volume'
				switch (field) {
					case 'volume':
						data = source.filter(item => filterMatch(conditionValue, item.volume, { condition: operator }));
						return data.length ? data : [];
					// Add more cases for other numeric fields as needed
					default:
						return source.length ? source : [];
				}
			}

			// Switch case for regular string fields (title, type, keyword, etc.)
			switch (querySelector) {
				case 'type':
					data = source.filter(item => filterMatch(queryValue, item.task_type, { partial: true }));
					return data.length ? data : [];
				case 'title':
					data = source.filter(item => filterMatch(queryValue, item.title, { partial: true }));
					return data.length ? data : [];
				case 'url':
					data = source.filter(item => filterMatch(queryValue, item.page_url[0], { partial: true }));
					return data.length ? data : [];
				case 'keyword':
					data = source.filter(item => item.keyword_data[0] && filterMatch(queryValue, item.keyword_data[0].keyword, { partial: true }));
					return data.length ? data : [];
				case 'user':
					const [queryFirstName, queryLastName] = queryValue.split(' - ').map(name => name.trim().toLowerCase());
					data = source.filter(item =>
						Array.isArray(item.users) &&
						item.users.some(user =>
							user && filterMatch(queryFirstName, user.first_name, { partial: false }) &&
							filterMatch(queryLastName, user.last_name, { partial: false })
						)
					);
					return data.length ? data : [];
				default:
					return source.length ? source : [];
			}
		},
		setFilterData(newFilterData, keyConfig) {
            this.filterData = newFilterData;

            const hasAllFilter = newFilterData.some(filter => 
                filter.variable === 'all' || filter.type === 'all'
            );

            if (hasAllFilter) {
                let filteredReco = [];
                let filteredTasks = [];

                newFilterData.forEach(filter => {
                    if (filter.variable === 'all' || filter.type === 'all') {
                        Object.keys(keyConfig).forEach(filterType => {
                            if (filterType !== 'all') {
                                const keyConfigItem = keyConfig[filterType];
                                const tempFilter = [{
                                    ...filter,
                                    variable: keyConfigItem.key,
                                    type: keyConfigItem.type
                                }];
                                
                                const recoResults = applyFilter(this.recommendationsClone, tempFilter, keyConfig);
                                const taskResults = applyFilter(this.tasksClone, tempFilter, keyConfig);
                                
                                filteredReco = [...filteredReco, ...Object.values(recoResults).flat()];
                                filteredTasks = [...filteredTasks, ...Object.values(taskResults).flat()];
                            }
                        });
                    } else {
                        const recoResults = applyFilter(this.recommendationsClone, [filter], keyConfig);
                        const taskResults = applyFilter(this.tasksClone, [filter], keyConfig);
                        
                        filteredReco = [...filteredReco, ...Object.values(recoResults).flat()];
                        filteredTasks = [...filteredTasks, ...Object.values(taskResults).flat()];
                    }
                });

                const uniqueReco = Array.from(new Set(filteredReco.map(item => JSON.stringify(item))))
                    .map(str => JSON.parse(str));
                const uniqueTasks = Array.from(new Set(filteredTasks.map(item => JSON.stringify(item))))
                    .map(str => JSON.parse(str));

                this.recommendations = uniqueReco;
                this.tasks = uniqueTasks;
            } else {
                const filteredReco = applyFilter(this.recommendationsClone, this.filterData, keyConfig);
                const filteredTasks = applyFilter(this.tasksClone, this.filterData, keyConfig);

                this.recommendations = Object.values(filteredReco).flat();
                this.tasks = Object.values(filteredTasks).flat();
            }
        },
		async deleteRecommendation(recoId, market_id) {
			const response = await axios.post(
				'vuejs/delete_recommendation',
				{
					reco_id: recoId,
				},
				{ withCredentials: true },
			);

			if (response.data.success) {
				this.fetchData(market_id);
				useNotificationStore().open('success', 'Recommendation deleted');
			}
		},
		async fetchSchemaOrgStorage(link) {
			try {
				const response = await axios.post(
					'vuejs/get_schema_org_storage',
					{
						link: link,
					},
					{ withCredentials: true },
				);

				if (response.data.success) {
					return response.data.data;
				} else {
					console.error('Error fetching schema.org data:', response.data.message);
					return null;
				}
			} catch (error) {
				console.error('Error fetching schema.org data:', error);
				return null;
			}
		},
	},
	persist: true,
});
